import { useEffect, useState } from 'react'
import { View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { Button } from '@chillout/ui'
import { AdminScreen } from '../AdminScreen'
import { ADMIN_SECTION_ROUTE_BY_NAME } from '../adminRoutes'
import { SectionErrorState } from '../shared/SectionErrorState'
import { CardSetupSection } from './CardSetupSection'
import { CustomerPreviewSection } from './CustomerPreviewSection'
import { EarningRuleSection } from './EarningRuleSection'
import { MilestonesSection } from './MilestonesSection'
import { RewardItemsSection } from './RewardItemsSection'
import { SaveConfirmModal } from './SaveConfirmModal'
import { useLoyaltyConfig } from './useLoyaltyConfig'
import { useRewardItems } from './useRewardItems'
import { useSaveRewardItem } from './useSaveRewardItem'

function SectionSkeleton({ height }: { height: number }) {
  return (
    <View className="gap-5 rounded-xl border border-border bg-canvas-card p-6">
      <View className="gap-2">
        <View className="h-5 w-40 animate-pulse rounded bg-canvas-primary" />
        <View className="h-4 w-64 animate-pulse rounded bg-canvas-primary" />
      </View>
      <View className="animate-pulse rounded-lg bg-canvas-primary" style={{ height }} />
    </View>
  )
}

interface LoyaltyScreenProps {
  onDirtyChange: (dirty: boolean) => void
}

export function LoyaltyScreen({ onDirtyChange }: LoyaltyScreenProps) {
  const { t } = useTranslation()
  const route = ADMIN_SECTION_ROUTE_BY_NAME['AdminLoyalty']
  const loyalty = useLoyaltyConfig()

  useEffect(() => {
    onDirtyChange(loyalty.hasChanges)
  }, [loyalty.hasChanges, onDirtyChange])
  const rewardItemsQuery = useRewardItems()
  const rewardItems = rewardItemsQuery.data ?? []
  const saveRewardItemMutation = useSaveRewardItem()
  const [previewStampCount, setPreviewStampCount] = useState(5)

  return (
    <AdminScreen
      title={t(route.titleKey)}
      subtitle={t(route.subtitleKey)}
      actions={
        <Button
          label={t('loyalty.saveChanges')}
          onPress={loyalty.requestSave}
          disabled={!loyalty.hasChanges}
          fullWidth={false}
        />
      }
    >
      {loyalty.isError ? (
        <SectionErrorState
          message={t('loyalty.loadError')}
          retryLabel={t('common.retry')}
          onRetry={loyalty.onRetry}
        />
      ) : loyalty.isLoading ? (
        <View className="flex-col gap-8 2xl:flex-row 2xl:items-start 2xl:gap-4">
          <View className="min-w-0 flex-1 gap-8">
            <SectionSkeleton height={140} />
            <SectionSkeleton height={80} />
            <SectionSkeleton height={160} />
            <SectionSkeleton height={160} />
          </View>
          <View className="hidden 2xl:flex 2xl:w-[448px]">
            <SectionSkeleton height={420} />
          </View>
        </View>
      ) : (
        <View className="flex-col gap-8 2xl:flex-row 2xl:items-start 2xl:gap-4">
          <View className="min-w-0 flex-1 gap-8">
            <EarningRuleSection stepNumber={1} control={loyalty.control} />
            <CardSetupSection stepNumber={2} control={loyalty.control} />

            <View className="2xl:hidden">
              <CustomerPreviewSection
                variant="inline"
                config={loyalty.config}
                stampCount={previewStampCount}
                onStampCountChange={setPreviewStampCount}
              />
            </View>

            <RewardItemsSection
              stepNumber={3}
              rewardItems={rewardItems}
              assignedRewardItemIds={loyalty.assignedRewardItemIds}
              isLoading={rewardItemsQuery.isLoading}
              isError={rewardItemsQuery.isError}
              onRetry={() => void rewardItemsQuery.refetch()}
              onSaveItem={(input) => saveRewardItemMutation.mutateAsync(input)}
              isSaving={saveRewardItemMutation.isPending}
              isSaveError={saveRewardItemMutation.isError}
              onResetSaveState={() => saveRewardItemMutation.reset()}
            />
            <MilestonesSection stepNumber={4} control={loyalty.control} rewardItems={rewardItems} />
          </View>

          <View className="hidden 2xl:flex 2xl:w-[448px] web:2xl:sticky web:2xl:top-8">
            <CustomerPreviewSection
              variant="sidebar"
              config={loyalty.config}
              stampCount={previewStampCount}
              onStampCountChange={setPreviewStampCount}
            />
          </View>
        </View>
      )}

      {loyalty.pendingConfig ? (
        <SaveConfirmModal
          visible
          serverConfig={loyalty.serverConfig}
          pendingConfig={loyalty.pendingConfig}
          rewardItems={rewardItems}
          saving={loyalty.isSaving}
          error={loyalty.saveError}
          onConfirm={loyalty.confirmSave}
          onCancel={loyalty.cancelSave}
        />
      ) : null}
    </AdminScreen>
  )
}
